import { useState, useEffect } from 'react';

interface ColorScheme {
  primary?: string;
  secondary?: string;
  accent?: string;
  background?: string;
  text?: string;
}

interface Typography {
  fontFamily?: string;
  fontSize?: string;
  fontWeight?: string;
}

interface Spacing {
  cardPadding?: string;
  gap?: string;
  borderRadius?: string;
}

export interface PricingTableConfig {
  planOrder: string[];
  layout: string;
  columnsPerRow: number;
  highlightedPlanId: string | null;
  sectionOrder: string[];
  featureOrder: Record<string, string[]> | null;
  colorScheme: ColorScheme | null;
  typography: Typography | null;
  spacing: Spacing | null;
  shadow: string | null;
  showAnnualToggle: boolean;
  showFeatures: boolean;
  showDescription: boolean;
  showCTA: boolean;
  ctaStyle: string;
  customCSS: string | null;
}

interface UsePricingTableConfigOptions {
  appId: string;
  apiUrl?: string;
}

interface UsePricingTableConfigResult {
  config: PricingTableConfig | null;
  loading: boolean;
  error: Error | null;
}

export function usePricingTableConfig({
  appId,
  apiUrl = 'https://use-pricing.vercel.app',
}: UsePricingTableConfigOptions): UsePricingTableConfigResult {
  const [config, setConfig] = useState<PricingTableConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchConfig = async (): Promise<void> => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${apiUrl}/api/public/pricing-table-config/${appId}`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch pricing table config: ${response.statusText}`);
        }

        const data = await response.json();

        if (!data.success) {
          throw new Error(data.error || 'Failed to fetch pricing table config');
        }

        setConfig(data.data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Unknown error occurred'));
        setConfig(null);
      } finally {
        setLoading(false);
      }
    };

    if (appId) {
      fetchConfig();
    }
  }, [appId, apiUrl]);

  return { config, loading, error };
}
